/**
 * HostRegistrationForm — Register a new infrastructure host (Xen/AWS)
 */

import React, { useState } from 'react';
import { useInfraStore } from '@/stores/infrastructure/infra-store';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const HostRegistrationForm: React.FC<Props> = ({ open, onClose }) => {
  const { registerHost, isLoading, error: storeError } = useInfraStore();
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    provider: 'xen' as 'xen' | 'aws',
    host: '',
    username: '',
    password: '',
    port: 443,
    mode: 'safe',
    description: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.displayName || !form.host || !form.username) {
      setError('Please fill in all required fields.');
      return;
    }

    const result = await registerHost({
      ...form,
      purpose: [],
    });

    if (result) {
      setForm({ name: '', displayName: '', provider: 'xen', host: '', username: '', password: '', port: 443, mode: 'safe', description: '' });
      onClose();
    }
  };

  const update = (field: string, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Register Infrastructure Host</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Host Name *</Label>
              <Input
                id="name"
                placeholder="XEN_HOST_1"
                value={form.name}
                onChange={(e) => update('name', e.target.value.toUpperCase())}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                placeholder="Primary Xen Server"
                value={form.displayName}
                onChange={(e) => update('displayName', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider</Label>
              <Select value={form.provider} onValueChange={(v) => update('provider', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xen">Xen / XCP-ng</SelectItem>
                  <SelectItem value="aws" disabled>AWS EC2 (coming soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mode">Operational Mode</Label>
              <Select value={form.mode} onValueChange={(v) => update('mode', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="safe">Safe (human only)</SelectItem>
                  <SelectItem value="supervised">Supervised (agent + approval)</SelectItem>
                  <SelectItem value="autonomous">Autonomous (agent independent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="host">Host Address *</Label>
              <Input
                id="host"
                placeholder="10.10.254.2"
                value={form.host}
                onChange={(e) => update('host', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="port">Port</Label>
              <Input
                id="port"
                type="number"
                value={form.port}
                onChange={(e) => update('port', parseInt(e.target.value) || 443)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                placeholder="root"
                value={form.username}
                onChange={(e) => update('username', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Primary Xen host for development VMs"
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={2}
            />
          </div>

          {(error || storeError) && (
            <p className="text-sm text-destructive">{error || storeError}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Registering...' : 'Register Host'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
